// Right-click menu on the board: clipboard, duplicate, arrange, open/edit,
// export and delete for the selection; paste, select all and zoom on empty
// board. Shift+F10 or the context-menu key open it from the keyboard.
import type { BoardObject } from '../../shared/types.ts';
import type { Editor } from '../editor.ts';
import { MOD_LABEL } from '../keyboard.ts';
import { isTextBearing } from '../text/placement.ts';
import { h } from '../util/dom.ts';
import { union } from '../util/geometry.ts';

export type MenuAction =
  | 'cut' | 'copy' | 'paste' | 'duplicate' | 'delete'
  | 'front' | 'back'
  | 'open-card' | 'edit-text' | 'rename-frame' | 'rename-audio' | 'download-audio'
  | 'stack-column' | 'unstack-column' | 'add-column'
  | 'export-png' | 'export-svg'
  | 'select-all' | 'zoom-fit';

export interface MenuEntry { action: MenuAction; label: string; hint?: string; danger?: boolean }
export type MenuItemOrSep = MenuEntry | 'separator';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const SHIFT = isMac ? '⇧' : 'Shift+';
const key = (k: string) => (isMac ? `${MOD_LABEL}${k}` : `${MOD_LABEL}+${k}`);

/** What the menu offers, from what was right-clicked. Pure, for tests. */
export function contextMenuItems(selection: readonly BoardObject[], readOnly: boolean): MenuItemOrSep[] {
  const out: MenuItemOrSep[] = [];
  const group = (...entries: (MenuEntry | false)[]) => {
    const kept = entries.filter((e): e is MenuEntry => !!e);
    if (!kept.length) return;
    if (out.length) out.push('separator');
    out.push(...kept);
  };
  if (!selection.length) {
    group(!readOnly && { action: 'paste', label: 'Paste here', hint: key('V') }, { action: 'select-all', label: 'Select all', hint: key('A') });
    group({ action: 'zoom-fit', label: 'Zoom to fit', hint: `${SHIFT}1` });
    return out;
  }
  const one = selection.length === 1 ? selection[0]! : null;
  const n = selection.length;
  group(
    !readOnly && { action: 'cut', label: 'Cut', hint: key('X') },
    { action: 'copy', label: 'Copy', hint: key('C') },
    !readOnly && { action: 'paste', label: 'Paste here', hint: key('V') },
    !readOnly && { action: 'duplicate', label: 'Duplicate', hint: key('D') },
  );
  group(
    !readOnly && { action: 'front', label: 'Bring to front', hint: ']' },
    !readOnly && { action: 'back', label: 'Send to back', hint: '[' },
  );
  group(
    !readOnly && one?.type === 'card' && { action: 'open-card', label: 'Open card', hint: '↵' },
    !readOnly && !!one && isTextBearing(one) && { action: 'edit-text', label: 'Edit text', hint: '↵' },
    !readOnly && one?.type === 'frame' && { action: 'rename-frame', label: one.layout === 'column' ? 'Rename column' : 'Rename frame' },
    !readOnly && one?.type === 'audio' && { action: 'rename-audio', label: 'Rename' },
  );
  // Audio clips (spec §19.2): their file, for playing elsewhere; viewers can save it too.
  group(one?.type === 'audio' && { action: 'download-audio', label: 'Download audio' });
  // Kanban columns (spec §13.6).
  group(
    !readOnly && one?.type === 'frame' && one.layout === 'column' && { action: 'add-column', label: 'Add column to the right' },
    !readOnly && one?.type === 'frame' && one.layout !== 'column' && { action: 'stack-column', label: 'Stack cards as a column' },
    !readOnly && one?.type === 'frame' && one.layout === 'column' && { action: 'unstack-column', label: 'Stop stacking' },
  );
  const what = n === 1 ? 'selection' : `${n} objects`;
  group(
    { action: 'export-png', label: `Export ${what} as PNG` },
    { action: 'export-svg', label: `Export ${what} as SVG` },
  );
  group(!readOnly && { action: 'delete', label: n === 1 ? 'Delete' : `Delete ${n} objects`, hint: '⌫', danger: true });
  return out;
}

export interface ContextMenuOptions {
  exportAs: (format: 'png' | 'svg') => void;
  notify: (message: string) => void;
}

export function bindContextMenu(ed: Editor, opts: ContextMenuOptions): void {
  const canvas = ed.layers.interactiveCanvas;
  let menu: HTMLDivElement | null = null;
  let offCamera: (() => void) | null = null;

  const close = () => {
    if (!menu) return;
    menu.remove();
    menu = null;
    document.removeEventListener('pointerdown', outside, true);
    window.removeEventListener('blur', close);
    window.removeEventListener('resize', close);
    offCamera?.();
    offCamera = null;
  };
  const outside = (e: Event) => { if (menu && !menu.contains(e.target as Node)) close(); };

  function run(action: MenuAction, at: [number, number]): void {
    switch (action) {
      case 'cut': return ed.copySelection(true);
      case 'copy': return ed.copySelection();
      case 'paste':
        void ed.pasteFromClipboard(at).then((r) => {
          if (r === 'blocked') opts.notify(`Your browser didn't let the board read the clipboard. Press ${key('V')} to paste instead.`);
          else if (r === 'empty') opts.notify('There is nothing to paste.');
        });
        return;
      case 'duplicate': return ed.duplicate();
      case 'delete': return ed.deleteSelection();
      case 'front': return ed.bringToFront();
      case 'back': return ed.sendToBack();
      case 'open-card': return ed.openCard([...ed.selection][0]!);
      case 'edit-text': return ed.startTextEdit([...ed.selection][0]!, { caret: 'all' });
      case 'rename-frame': return ed.renameFrame([...ed.selection][0]!);
      case 'rename-audio': return ed.renameAudio([...ed.selection][0]!);
      case 'download-audio': return ed.downloadAudio([...ed.selection][0]!);
      case 'stack-column': return ed.setColumn([...ed.selection][0]!, true);
      case 'unstack-column': return ed.setColumn([...ed.selection][0]!, false);
      case 'add-column': return ed.addColumnRight([...ed.selection][0]!);
      case 'export-png': return opts.exportAs('png');
      case 'export-svg': return opts.exportAs('svg');
      case 'select-all':
        ed.setTool('select');
        return ed.selectAll();
      case 'zoom-fit': return ed.zoomToFit();
    }
  }

  function open(clientX: number, clientY: number, world: [number, number], fromKeyboard: boolean): void {
    close();
    const items = contextMenuItems(ed.selectedObjects(), ed.readOnly);
    const el = h('div', { class: 'panel menu context-menu', role: 'menu', 'aria-label': 'Actions', tabindex: '-1' });
    for (const item of items) {
      if (item === 'separator') {
        el.append(h('div', { class: 'menu-sep', role: 'separator' }));
        continue;
      }
      const b = h('button', { type: 'button', role: 'menuitem', class: item.danger ? 'menu-item danger' : 'menu-item', 'data-action': item.action }, [
        h('span', {}, [item.label]), ...(item.hint ? [h('kbd', {}, [item.hint])] : []),
      ]);
      b.addEventListener('click', () => {
        close();
        run(item.action, world);
      });
      el.append(b);
    }
    el.addEventListener('keydown', (e) => {
      e.stopPropagation(); // not the board's shortcuts
      const buttons = [...el.querySelectorAll<HTMLButtonElement>('.menu-item')];
      const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? (i + 1) % buttons.length : (i <= 0 ? buttons.length : i) - 1;
        buttons[next]?.focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        buttons[e.key === 'Home' ? 0 : buttons.length - 1]?.focus();
      }
    });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    document.body.append(el);
    const w = el.offsetWidth, hgt = el.offsetHeight;
    const x = clientX + w > innerWidth - 8 ? Math.max(8, clientX - w) : clientX;
    const y = clientY + hgt > innerHeight - 8 ? Math.max(8, clientY - hgt) : clientY;
    el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    menu = el;
    if (fromKeyboard) (el.querySelector('.menu-item') as HTMLElement | null)?.focus();
    else el.focus({ preventScroll: true });
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('blur', close);
    window.addEventListener('resize', close);
    offCamera = ed.events.on('camera', close);
  }

  // Right-click on the board; from the keyboard (Shift+F10, the menu key) the
  // event targets the page itself, with no pointer position.
  document.addEventListener('contextmenu', (e) => {
    const fromKeyboard = e.target === document.body;
    if (e.target !== canvas && !fromKeyboard) return; // text fields etc. keep the browser's menu
    e.preventDefault();
    if (ed.editingId) return;
    const r = canvas.getBoundingClientRect();
    let sx = e.clientX - r.left, sy = e.clientY - r.top;
    if (fromKeyboard) {
      const b = union(ed.selectedObjects().map((o) => ed.boundsOf(o)));
      [sx, sy] = b ? ed.camera.worldToScreen(b.x + b.w / 2, b.y + b.h / 2) : [r.width / 2, r.height / 2];
    }
    const world = ed.camera.screenToWorld(sx, sy);
    if (!fromKeyboard) ed.selectForMenu(world[0], world[1]);
    open(sx + r.left, sy + r.top, world, fromKeyboard);
  });
}
